import { useCallback, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions as cloudFunctions } from '../firebase';

type CheckoutResponse = {
  url?: string;
  sessionId?: string;
};

interface CheckoutOptions {
  priceId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export const useStripeCheckout = () => {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const startCheckout = useCallback(
    async (options?: CheckoutOptions) => {
      setIsCheckoutLoading(true);
      try {
        const callable = httpsCallable(cloudFunctions, 'createStripeCheckoutSession');
        const origin = window.location.origin;
        const payload = {
          successUrl: options?.successUrl || `${origin}/billing/success`,
          cancelUrl: options?.cancelUrl || `${origin}/billing/cancel`,
        };

        const result = await callable(payload);
        const data = result.data as CheckoutResponse;

        if (data.url) {
          window.location.assign(data.url);
          return;
        }

        if (data.sessionId) {
          throw new Error('Checkout session missing URL. Please verify the Cloud Function configuration.');
        }

        throw new Error('Unable to start checkout session');
      } finally {
        setIsCheckoutLoading(false);
      }
    },
    []
  );

  const openBillingPortal = useCallback(async (returnUrl?: string) => {
    setIsPortalLoading(true);
    try {
      const callable = httpsCallable(cloudFunctions, 'createStripePortalSession');
      const result = await callable({
        returnUrl: returnUrl || `${window.location.origin}/billing`,
      });
      const data = result.data as { url?: string };
      if (!data.url) {
        throw new Error('Billing portal URL was not returned');
      }
      window.location.assign(data.url);
    } finally {
      setIsPortalLoading(false);
    }
  }, []);

  return {
    startCheckout,
    openBillingPortal,
    isCheckoutLoading,
    isPortalLoading,
  };
};

